import * as env from '@/common/environment';
import { MINUTE } from './date';
import { checkAiABTestingPassed } from './bot/lib';

// TODO: move to AI Service Utils, separate types

type UserId = string | number | undefined;

interface IsUserEligibleForAiFlowParams {
  userId: UserId;
  userLoyaltyTier?: string;
  // Check if OpenAI is down
  openAiDown?: boolean | null;
  // Skip AI flow per specific entity ID
  skipAiChat?: boolean;
  // Check if AI Flow enabled per brand/disabled by Chatbot
  aiFlowEnabled?: boolean | null;
  // Check if AI AB testing is enabled
  aiAbTestingEnabled?: boolean | null;
  // AB test percentage value
  abTestPercentage?: number | null;
  // How long the user should wait before being able to connect to AI again in mins
  aiEndUserComeBackTime?: number | null;
  // Check metadata rules are enabled
  enableMetadataRules?: boolean;
}

export const getRecentlyConnectedAiKey = (userId: UserId): string =>
  `AI-${userId}-connect`;

export const hasRecentlyConnectedAI = (userId: UserId, conversationCooldownMins: number | null = 0): boolean => {
  if (!userId || !conversationCooldownMins) {
    return false;
  }

  const {
    bypassAbTestingUserIds,
  }: { bypassAbTestingUserIds?: string[] } = env.get('AI') || {
    bypassAbTestingUserIds: [],
  };

  // Specified users should see AI without any restrictions
  if (bypassAbTestingUserIds?.includes(String(userId))) {
    return false;
  }

  // Time in milliseconds or null value
  const recentlyConnectedValue = localStorage.getItem(
    getRecentlyConnectedAiKey(userId)
  );
  if (!recentlyConnectedValue) return false;

  const recentlyConnectedTimestamp = Number(recentlyConnectedValue);
  const conversationCooldownMs = conversationCooldownMins * MINUTE;

  return Date.now() - recentlyConnectedTimestamp < conversationCooldownMs;
};

export const setRecentlyConnectedAI = (userId?: UserId): void => {
  if (!userId) {
    return;
  }

  localStorage.setItem(getRecentlyConnectedAiKey(userId), String(Date.now()));
};

const BYPASS_AI_FLOW_LOYALTY_TIERS = ['platinum', 'diamond'];

export const isUserEligibleForAiFlow = (
  params: IsUserEligibleForAiFlowParams
): boolean => {
  const {
    userId,
    userLoyaltyTier,
    skipAiChat = false,
    aiFlowEnabled: aiFlowEnabledOverride = null,
    enableMetadataRules = false,
    openAiDown = false,
    abTestPercentage = 0,
    aiAbTestingEnabled = false,
    aiEndUserComeBackTime = 0,
  } = params;

  // Use AI Flow enabled value from the environment if not provided
  const aiFlowEnabled = aiFlowEnabledOverride ?? (env.get('aiFlow') || false);
  const abTestPassed = checkAiABTestingPassed({
    userId,
    abValue: abTestPercentage,
    enableAiAbTesting: aiAbTestingEnabled,
  });
  const didUserRecentlyConnectToAI = hasRecentlyConnectedAI(userId, aiEndUserComeBackTime);
  const userBypassAiFlowLoyaltyTier = BYPASS_AI_FLOW_LOYALTY_TIERS.includes(
    (userLoyaltyTier || '').toLowerCase()
  );

  return Boolean(
    aiFlowEnabled &&
    abTestPassed &&
    !enableMetadataRules &&
    !openAiDown &&
    !skipAiChat &&
    !didUserRecentlyConnectToAI && 
    !userBypassAiFlowLoyaltyTier
  );
};
